import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, Loader2 } from "lucide-react";

// shadcn ui
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { 
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "components/ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "components/ui/table";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "components/ui/card";
import {
  Pagination as Pager,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "components/ui/pagination";
import { SURVEY_TYPE, STATUS } from "data/AdminData"

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch } from "react-redux";

import {
  UPDATE_SURVEY_QUESTION_PAGE_NUMBER,
  SG_GET_SURVEY_QUESTIONS,
} from "constants/actions";


const Categories = ({ setOrgName }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  let ReloadPage = searchParams.get("reload");

  // const [organizationSelect, setOrganizationSelect] = useState([])
  const [statusSelected, setStatusSelected] = useState(undefined)
  const [surveyTypeSelected, setSurveyTypeSelected] = useState(undefined)
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchBox, setSearchBox] = useState('')

  const { get_survey_question,
    get_survey_pageNumber } = useSelector(
    (state) => ({
      get_survey_question: state.surveyquestion.survey_question,
      get_survey_pageNumber: state.surveyquestion.survey_question.pageNumber,
    }),
    shallowEqual
  );

  useEffect(() => {
      const payloadParts = [];
      if (page) payloadParts.push(`page=${page}`);
      if (searchTerm) payloadParts.push(`sort_order=${searchTerm}`);
      if (statusSelected !== undefined) payloadParts.push(`status=${statusSelected}`);
      if (surveyTypeSelected !== undefined) payloadParts.push(`survey_type=${surveyTypeSelected}`);
      dispatch({
        type: SG_GET_SURVEY_QUESTIONS,
        payload: payloadParts.join('&')
      });
  }, [dispatch, page, searchTerm, statusSelected, surveyTypeSelected]);

  console.log("searchTerm", searchTerm)

  useEffect(() => {
    const incoming = get_survey_question?.results;
    setResults(Array.isArray(incoming) ? incoming : []);

    const totalCount = Number(get_survey_question?.count) || 0;
    const pageSize = Number(get_survey_question?.page_size) || 1;
    const computedTotalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    setTotalPages(computedTotalPages);
    setLoading(false);
  }, [get_survey_question]);

  useEffect(() => {
    setPage(get_survey_pageNumber ? get_survey_pageNumber : 1)
  }, [get_survey_pageNumber]);

  useEffect(() => {
    if (ReloadPage) {
      dispatch({
        type: SG_GET_SURVEY_QUESTIONS,
        payload: page ? `page=${page}` : ''
      });
    }
  }, [ReloadPage, navigate, dispatch, page]);

  function goToPage(newPage) {
    if (!newPage || newPage === page) return;
    setLoading(true)
    setPage(newPage);
    dispatch({
      type: UPDATE_SURVEY_QUESTION_PAGE_NUMBER,
      payload: newPage,
    });
    const timer = setTimeout(() => {
      setLoading(false)
    }, 500);
    return timer
  }

  function UpdateQuestions() {}

  function onSearchSubmit(e) {
    if (e) e.preventDefault();
    setSearchTerm(searchBox);
  }

  const paginationItems = useMemo(() => {
    const pages = Number(totalPages) || 0;
    const current = Number(page) || 1;
    if (pages <= 1) return [];

    const items = [];
    const addPage = (p) => items.push({ type: "page", page: p, active: p === current });
    const addEllipsis = (key) => items.push({ type: "ellipsis", key });

    addPage(1);
    const start = Math.max(2, current - 1);
    const end = Math.min(pages - 1, current + 1);

    if (start > 2) addEllipsis("start");
    for (let p = start; p <= end; p += 1) addPage(p);
    if (end < pages - 1) addEllipsis("end");
    if (pages > 1) addPage(pages);
    return items;
  }, [page, totalPages]);

  const hasResults = (results || []).length > 0;

  return (
    <div className="mb-8 space-y-6">
      <Card className="border-border/60 shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <CardTitle className="text-base font-semibold">Filters</CardTitle>
          <CardDescription>Search by sort order or narrow by status and survey type.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[minmax(220px,320px)_repeat(2,minmax(200px,1fr))_auto]">
          <form onSubmit={onSearchSubmit} className="flex items-center gap-2">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchBox}
                onChange={(e) => setSearchBox(e.target.value)}
                placeholder="Search by sort order"
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>

          <Select value={surveyTypeSelected} onValueChange={(v) => { setSurveyTypeSelected(v); setPage(1); }}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by survey type" />
            </SelectTrigger>
            <SelectContent>
              {SURVEY_TYPE?.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusSelected} onValueChange={(v) => { setStatusSelected(v); setPage(1); }}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS?.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex w-full items-center justify-end gap-2">
            <Button variant="outline" type="button" onClick={() => {
              setSurveyTypeSelected(undefined);
              setStatusSelected(undefined);
              setSearchBox("");
              setSearchTerm("");
              setPage(1);
            }}>
              Reset
            </Button>
            <Button onClick={() => navigate(`/app/questions/new/`)}>
              <Plus className="mr-1 h-4 w-4" />
              Add new
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="relative mt-6">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {!hasResults ? (
          <Card className="p-8 text-center text-muted-foreground">
            <CardTitle className="mb-1 text-base font-medium">No questions found</CardTitle>
            <CardDescription>Adjust filters or create a new question.</CardDescription>
          </Card>
        ) : (
        <Table>
          <TableHeader className="bg-background">
            <TableRow>
              <TableHead className="w-[80px] sticky top-0 bg-background z-20">ID</TableHead>
              <TableHead className="sticky top-0 bg-background z-20">Sort Order</TableHead>
              <TableHead className="sticky top-0 bg-background z-20">Name</TableHead>
              <TableHead className="sticky top-0 bg-background z-20">Type</TableHead>
              <TableHead className="sticky top-0 bg-background z-20">Status</TableHead>
              <TableHead className="sticky top-0 bg-background z-20">Updated</TableHead>
              <TableHead className="sticky top-0 bg-background z-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(results || []).map((row) => {
              const typeLabel = row?.survey_type && SURVEY_TYPE.filter((sy) => sy.value === row?.survey_type)?.[0]?.label
              const statusLabel = row?.status && STATUS.filter((sy) => sy.value === row?.status)?.[0]?.label
              return (
                <TableRow key={row?.id}>
                  <TableCell className="font-mono">{row?.id}</TableCell>
                  <TableCell>{row?.sort_order}</TableCell>
                  <TableCell className="font-medium">{row?.name}</TableCell>
                  <TableCell>{typeLabel || row?.survey_type}</TableCell>
                  <TableCell>{statusLabel || row?.status}</TableCell>
                  <TableCell>{row?.updated_at ? new Date(row?.updated_at).toLocaleDateString() : ""}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/app/questions/details/${row?.id}`)}>
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        )}
      </div>

      <div className="py-6">
        <Pager>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious href="#" onClick={(e) => { e.preventDefault(); goToPage(page > 1 ? page - 1 : 1); }} />
            </PaginationItem>
            {paginationItems.map((item, idx) => (
              item.type === "ellipsis" ? (
                <PaginationItem key={`ellipsis-${item.key}-${idx}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={`page-${item.page}`}>
                  <PaginationLink href="#" isActive={item.active} onClick={(e) => { e.preventDefault(); goToPage(item.page); }}>
                    {item.page}
                  </PaginationLink>
                </PaginationItem>
              )
            ))}
            <PaginationItem>
              <PaginationNext href="#" onClick={(e) => { e.preventDefault(); goToPage(page < totalPages ? page + 1 : totalPages); }} />
            </PaginationItem>
          </PaginationContent>
        </Pager>
      </div>
    </div>
  );
};

export default Categories;

